import React, { useState } from 'react';

interface AnalysisResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  analysis: {
    id: string;
    title: string;
    type: string;
    content: any;
    createdAt: string;
  };
}

const getAnalysisTypeInfo = (type: string) => {
  const typeInfo: Record<string, { icon: string; description: string; category: string }> = {
    // Core Analysis - 2 araç
    'summary': { icon: '📄', description: 'Belgelerin genel özetini çıkarır, ana konuları ve temel olayları belirler', category: 'Temel Analiz' },
    'evidence': { icon: '🔍', description: 'Mevcut delilleri kataloglar, eksik delilleri tespit eder ve delil toplama planı önerir', category: 'Temel Analiz' },

    // Planning - 2 araç
    'timeline': { icon: '⏰', description: 'Olayların kronolojik sırasını çıkarır ve süreç haritası oluşturur', category: 'Planlama' },
    'questions': { icon: '❓', description: 'Açıklığa kavuşturulması gereken konuları belirler ve stratejik sorular oluşturur', category: 'Planlama' },

    // Strategy - 7 araç
    'strategy': { icon: '🎯', description: 'Ana hukuki stratejiyi geliştirir, alternatif yaklaşımları önerir', category: 'Strateji' },
    'contradictions': { icon: '⚡', description: 'Belgelerdeki çelişkileri bulur, tarihsel tutarsızlıkları tespit eder', category: 'Strateji' },
    'perception': { icon: '🎭', description: 'Davayı film gibi kurgular, hangi bilgiyi önce hangisini sonra vereceğine karar verir', category: 'Strateji' },
    'jenga': { icon: '🏗️', description: 'Karşı tarafın en zayıf argümanlarını bulur, hangi argümanın çürütülmesi diğerlerini çökertir gösterir', category: 'Strateji' },
    'synthesizer': { icon: '🔗', description: 'Mevcut zayıf argümanları birleştirerek daha güçlü yeni argümanlar oluşturur', category: 'Strateji' },
    'flow': { icon: '🌊', description: 'Argümanların nasıl birbirine bağlandığını ve hangi noktalarda güçlendirme gerektiğini gösterir', category: 'Strateji' },
    'fibonacci': { icon: '📊', description: 'Argümanları katmanlar halinde sıralar. Her katman bir öncekini destekler ve güçlendirir', category: 'Strateji' },

    // Advanced - 11 araç
    'opponent': { icon: '👥', description: 'Karşı tarafın güçlü yanlarını belirler, zayıf noktalarını tespit eder', category: 'Gelişmiş Analiz' },
    'negotiation': { icon: '🤝', description: 'Pazarlık noktalarını belirler, müzakere taktikleri önerir', category: 'Gelişmiş Analiz' },
    'connections': { icon: '🔗', description: 'Alakasız görünen olaylar arasında gizli ilişkiler bulur', category: 'Gelişmiş Analiz' },
    'anomaly': { icon: '⚠️', description: 'Alışılmadık zaman, miktar, davranış paternlerini tespit eder. Normal dışı durumları gösterir', category: 'Gelişmiş Analiz' },
    'missing': { icon: '🧩', description: 'Olması gereken ama olmayan belgeleri, söylenmeyenleri tespit eder', category: 'Gelişmiş Analiz' },
    'gestalt': { icon: '💭', description: 'İnsan beyni eksik bilgileri otomatik tamamlar. Hakimin zihninin bu özelliğini lehimize kullanmayı gösterir', category: 'Gelişmiş Analiz' },
    'crispr': { icon: '✏️', description: 'Argümanlardaki zayıf kısımları tespit eder, gereksizleri çıkarır, eksikleri ekler. Öncesi-sonrası düzeltme önerileri', category: 'Gelişmiş Analiz' },
    'dualprocess': { icon: '⚖️', description: 'Hangi argümanların duyguya, hangilerinin mantığa hitap ettiğini belirler. Ne zaman ve nasıl kullanacağınızı gösterir', category: 'Gelişmiş Analiz' },
    'weber': { icon: '💵', description: 'Para, süre, hasar gibi sayıları en etkili şekilde nasıl sunacağınızı gösterir. Alternatif sunum şekilleri önerir', category: 'Gelişmiş Analiz' },
    'vonrestorff': { icon: '⭐', description: 'Kritik delilleri nasıl öne çıkaracağınızı gösterir. Farklı ve sıradışı olan daha iyi hatırlanır', category: 'Gelişmiş Analiz' },
    'cognitiveload': { icon: '📚', description: 'Hakimi yormadan bilgi sunmayı gösterir. Aynı anda 4-5 bilgiden fazla işlenemez, sunumu optimize eder', category: 'Gelişmiş Analiz' },
    'mereexposure': { icon: '🔁', description: 'Ana mesajları kaç kez ve nasıl tekrar edeceğinizi gösterir. Optimal: 5-9 tekrar', category: 'Gelişmiş Analiz' },

    // Specialized - 10 araç
    'mindmap': { icon: '📋', description: 'Davanın tüm unsurlarını kategorize eder: taraflar, deliller, süreç, kritik noktalar. Tek sayfada genel bakış', category: 'Özel Araçlar' },
    'quantum': { icon: '🔀', description: 'Belirsiz durumları tespit eder (hem A hem B doğru olabilir). Her ihtimal için hazırlık stratejisi oluşturur', category: 'Özel Araçlar' },
    'archaeology': { icon: '📅', description: 'Belgeleri zaman sırasına göre katmanlara ayırır (ilk/orta/son). Hikaye değişti mi tespit eder', category: 'Özel Araçlar' },
    'entanglement': { icon: '🔄', description: 'Hangi delillerin birbirine bağlı olduğunu gösterir. Bir delil çürütülürse diğerleri nasıl etkilenir', category: 'Özel Araçlar' },
    'crystal': { icon: '💎', description: 'Delilleri ağ yapısında organize eder: merkezi, bağlantı, yardımcı deliller. Yedek planı gösterir', category: 'Özel Araçlar' },
    'bayes': { icon: '📈', description: 'Her delilin davayı ne kadar güçlendirdiğini matematiksel olarak hesaplar', category: 'Özel Araçlar' },
    'zipf': { icon: '📝', description: 'Belgelerdeki metinlerin gerçek mi yapay zeka/kopya mi olduğunu tespit eder. Kelime çeşitliliği ve stil tutarlılığı analiz eder', category: 'Özel Araçlar' },
    'liwc': { icon: '🎯', description: 'Tanık ifadelerinde yalan işaretlerini tespit eder. Az "ben" kullanımı, çok olumsuz duygu, belirsiz tarih/yer', category: 'Özel Araçlar' },
    'tfidf': { icon: '🔑', description: 'Belgelerde en önemli ve nadir kelimeleri bulur. Hangi kelimeler kritik bilgi içeriyor gösterir', category: 'Özel Araçlar' }
  };

  return typeInfo[type] || { icon: '📊', description: 'Analiz sonuçları', category: 'Analiz' };
};

const formatAnalysisContent = (content: any) => {
  if (!content) return 'Analiz içeriği bulunamadı.';

  // If content has analysis property
  if (content.analysis) {
    return content.analysis;
  }

  // If content is string
  if (typeof content === 'string') {
    return content;
  }

  // If content is object, try to extract meaningful text
  if (typeof content === 'object') {
    const keys = Object.keys(content);
    const textKey = keys.find(key =>
      ['analysis', 'result', 'content', 'summary', 'output'].includes(key.toLowerCase())
    );

    if (textKey && content[textKey]) {
      return content[textKey];
    }

    // Fallback to JSON string
    return JSON.stringify(content, null, 2);
  }

  return String(content);
};

const AnalysisResultModal: React.FC<AnalysisResultModalProps> = ({
  isOpen,
  onClose,
  analysis
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  if (!isOpen) return null;

  const typeInfo = getAnalysisTypeInfo(analysis.type);
  const analysisContent = formatAnalysisContent(analysis.content);

  // Copy to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(analysisContent).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // Download as text file
  const handleDownload = () => {
    const blob = new Blob([analysisContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${analysis.type}-${analysis.id.slice(0, 8)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{typeInfo.icon}</span>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {analysis.title}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>📅 {new Date(analysis.createdAt).toLocaleString('tr-TR')}</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                    {typeInfo.category}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCopy}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {copySuccess ? (
                <>
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-green-600">Kopyalandı!</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Kopyala</span>
                </>
              )}
            </button>

            <button
              onClick={handleDownload}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>İndir</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Description */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-2">📋 Analiz Türü</h4>
            <p className="text-sm text-gray-600">{typeInfo.description}</p>
          </div>

          {/* Analysis Content */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">📊 Analiz Sonuçları</h4>
            <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                {analysisContent}
              </pre>
            </div>
          </div>

          {/* Analysis Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-500">
            <div>
              <span className="font-medium">📄 Analiz ID:</span> {analysis.id}
            </div>
            <div>
              <span className="font-medium">🔧 Analiz Türü:</span> {analysis.type}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisResultModal;